// client/src/components/TeamScreen.js
//
// Team tab — /teams/:teamId/team. Roster + coaches for the team.
//
// Layout: Coaches as hero card at top, then Roster section below.
// Fetches two endpoints in parallel (roster + coaches) and combines
// their loading/error states.
//
// v1 schema limitations worth remembering:
//   - No jersey numbers yet (PlayerTeam needs jersey_number column;
//     lives there not on PlayerProfiles because a player rostered on
//     two teams can wear different numbers)
//   - No positions yet (younger age groups don't have set positions;
//     older ones would — same PlayerTeam column, position varchar)
//   - Guardian contact info not shown to non-coach roles yet (privacy)
// Search "SCHEMA-TODO" when those columns exist.

import { useOutletContext } from 'react-router-dom';
import { useFetch } from '../hooks/useFetch';
import { PageTitle } from './shell/PageTitle';
import { Scrawl } from './mischief/Scrawl';

// ── CoachHero ───────────────────────────────────────────────────────────
// Hero card containing all coaches for this team. One anchor at the top,
// same visual weight as ScheduleScreen's hero. Handles 1 coach or many.
function CoachHero({ coaches }) {
  if (!coaches || coaches.length === 0) {
    return (
      <article className="rounded-2xl bg-paper border border-rule px-5 py-5">
        <div className="text-[10px] tracking-[0.16em] font-bold uppercase text-inkSub">
          Coaches
        </div>
        <p className="mt-2 text-[14px] text-inkSub">
          No coaches assigned yet.
        </p>
      </article>
    );
  }

  return (
    <article className="rounded-2xl bg-paper border border-rule px-5 py-5">
      <div className="text-[10px] tracking-[0.16em] font-bold uppercase text-inkSub">
        Coaches
      </div>
      <div className="mt-3 space-y-4">
        {coaches.map(c => <CoachEntry key={c.id} coach={c} />)}
      </div>
    </article>
  );
}

// ── CoachEntry ──────────────────────────────────────────────────────────
// One coach inside the hero. Name prominent (serif), role as inkSub
// caption, email as a tappable mailto link.
function CoachEntry({ coach }) {
  const fullName = [coach.first_name, coach.last_name].filter(Boolean).join(' ');
  const mailto = coach.email ? 'mailto:' + coach.email : null;

  return (
    <div>
      <div className="font-serif text-[22px] leading-tight text-ink">
        {fullName || 'Coach'}
      </div>
      {coach.role && (
        <div className="text-[11px] tracking-[0.08em] uppercase font-semibold text-inkSub mt-0.5">
          {coach.role}
        </div>
      )}
      {mailto && (
        <a href={mailto} className="inline-block mt-1 text-[13px] text-accentDeep underline decoration-dotted underline-offset-2 hover:text-ink">
          {coach.email}
        </a>
      )}
    </div>
  );
}

// ── RosterList ──────────────────────────────────────────────────────────
// Simple list of players — first and last name only for v1.
// SCHEMA-TODO: when jersey_number / position land on PlayerTeam, upgrade
// this to a `54px | 1fr | auto` row like ScheduleScreen's ListRow, with
// the number in the left column.
function RosterList({ roster }) {
  return (
    <div>
      {roster.map(p => {
        const fullName = [p.first_name, p.last_name].filter(Boolean).join(' ');
        return (
          <div
            key={p.id}
            className="py-3 border-t border-rule text-[15px] text-ink"
          >
            {fullName || '—'}
          </div>
        );
      })}
    </div>
  );
}

// ── SectionLabel ────────────────────────────────────────────────────────
// Matches ScheduleScreen's section header — small caps eyebrow with count.
function SectionLabel({ children, count }) {
  return (
    <div className="mt-8 mb-2 flex items-baseline justify-between">
      <div className="text-[10px] tracking-[0.16em] font-bold uppercase text-inkSub">
        {children}
      </div>
      {count != null && (
        <div className="text-[11px] text-inkSub">
          {count} {count === 1 ? 'player' : 'players'}
        </div>
      )}
    </div>
  );
}

// ── TeamScreen ──────────────────────────────────────────────────────────
export function TeamScreen() {
  const { team } = useOutletContext();

  // Two parallel fetches. useFetch's null-skip means these stay idle
  // until team.id is real, matching the pattern from ScheduleScreen.
  const {
    data: roster,
    loading: rosterLoading,
    error: rosterError,
  } = useFetch(team?.id ? `/teams/${team.id}/roster` : null);

  const {
    data: coaches,
    loading: coachesLoading,
    error: coachesError,
  } = useFetch(team?.id ? `/teams/${team.id}/coaches` : null);

  const loading = rosterLoading || coachesLoading;
  const error = rosterError || coachesError;

  if (loading) {
    return (
      <div className="px-5 pt-6 pb-8">
        <PageTitle eyebrow={team?.name?.toUpperCase() ?? 'TEAM'}>
          Team
        </PageTitle>
        <div className="mt-8">
          <div className="rounded-2xl bg-paper border border-rule px-5 py-5 animate-pulse">
            <div className="h-3 bg-rule rounded w-1/4" />
            <div className="mt-4 h-6 bg-rule rounded w-2/3" />
            <div className="mt-2 h-3 bg-rule rounded w-1/3" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-5 pt-6 pb-8">
        <PageTitle eyebrow={team?.name?.toUpperCase() ?? 'TEAM'}>
          Team
        </PageTitle>
        <div className="mt-6 rounded-2xl bg-paper border border-rule px-5 py-4">
          <h3 className="font-serif text-[20px] text-ink">Something's off.</h3>
          <Scrawl className="mt-2" color="inkSub">
            couldn't load the team — try refresh?
          </Scrawl>
        </div>
      </div>
    );
  }

  const rosterEmpty = !roster || roster.length === 0;
  const coachesEmpty = !coaches || coaches.length === 0;
  const totallyEmpty = rosterEmpty && coachesEmpty;

  return (
    <div className="px-5 pt-6 pb-8">
      <PageTitle eyebrow={team?.name?.toUpperCase() ?? 'TEAM'}>
        Team
      </PageTitle>

      {totallyEmpty ? (
        <div className="text-center pt-10 pb-4">
          <div className="font-serif text-[32px] text-ink leading-tight">
            No news
          </div>
          <Scrawl className="mt-2 inline-block" color="inkSub">
            check back for Roster updates! 🏒
          </Scrawl>
        </div>
      ) : (
        <>
          <div className="mt-6">
            <CoachHero coaches={coaches ?? []} />
          </div>

          {!rosterEmpty && (
            <>
              <SectionLabel count={roster.length}>Roster</SectionLabel>
              <RosterList roster={roster} />
            </>
          )}

          {rosterEmpty && !coachesEmpty && (
            <>
              <SectionLabel>Roster</SectionLabel>
              <p className="text-[14px] text-inkSub mt-2">
                No players on the roster yet.
              </p>
            </>
          )}
        </>
      )}
    </div>
  );
}